import { useEffect, useRef, useState } from "react";

import type { MeResponse } from "../api/auth.api.ts";
import { useLogout } from "../lib/auth.ts";

interface Props {
  user: NonNullable<MeResponse["user"]>;
  session: NonNullable<MeResponse["session"]>;
}

function initials(name: string | null, email: string | null): string {
  const source = name ?? email ?? "?";
  return source
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function UserMenu({ user, session }: Props) {
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white py-1 pl-1 pr-2.5 text-sm transition hover:bg-zinc-50"
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="h-6 w-6 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-semibold text-white">
            {initials(user.name, user.email)}
          </span>
        )}
        <span className="hidden text-zinc-700 sm:inline">
          {user.name ?? user.email ?? "Account"}
        </span>
      </button>
      {open ? (
        <div className="absolute right-0 mt-2 w-64 rounded-md border border-zinc-200 bg-white p-2 shadow-md">
          <div className="border-b border-zinc-100 px-2 pb-2">
            <p className="text-sm font-medium text-zinc-900">
              {user.name ?? "Signed in"}
            </p>
            {user.email ? (
              <p className="truncate text-xs text-zinc-500">{user.email}</p>
            ) : null}
            <p className="mt-1 text-[10px] uppercase tracking-wide text-zinc-400">
              via {session.provider}
            </p>
          </div>
          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="mt-1 w-full rounded-sm px-2 py-1.5 text-left text-sm text-zinc-700 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:text-zinc-400"
          >
            {logout.isPending ? "Signing out…" : "Sign out"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
